import { GetStateByStateKeyResponse } from '@iwoca/lapi-client/edge';
import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';

import { TFormValues, TPerson, TSerialisedAddress } from './MSHForm.types';

const removedUndefinedValues = (object: { [k: string]: any }) =>
    Object.fromEntries(
        Object.entries(object).filter(([, val]) => {
            return val !== undefined;
        })
    );

export const convertDeserialisedPersonToFormValues = (
    deserialisedPerson: TPerson
) => {
    const { uid, title, firstName, lastName, dateOfBirth, roles, addresses } =
        deserialisedPerson;
    const { day, month, year } = dateOfBirth!;
    const address = addresses![0];
    const sanitisedAddress = removedUndefinedValues(address);

    const mappedFormValues = {
        uid: uid,
        title: title,
        firstName: firstName,
        lastName: lastName,
        day: day,
        month: month,
        year: year,
        director: roles?.includes('director'),
        shareholder: roles?.includes('shareholder'),
        guarantor: roles?.includes('guarantor'),
        address: sanitisedAddress
    };

    return removedUndefinedValues(mappedFormValues);
};

export const createCustomerStateWithNewOrUpdatedPerson = (
    values: TFormValues,
    customerState: GetStateByStateKeyResponse
) => {
    return produce(customerState, (draft) => {
        let draftPerson = draft.data.application!.people!.find(
            (existingPerson) => existingPerson.uid === values.uid
        );
        if (draftPerson === undefined) {
            draftPerson = {
                uid: values.uid || uuidv4(),
                title: values.title,
                first_name: values.firstName,
                last_name: values.lastName,
                date_of_birth: `${values.year}-${values.month}-${values.day}`,
                roles: [],
                residential_addresses: []
            };
            draft.data.application!.people!.push(draftPerson);
        }

        for (const { role, shouldBeApplied } of [
            { role: 'director', shouldBeApplied: values.director },
            { role: 'shareholder', shouldBeApplied: values.shareholder },
            { role: 'guarantor', shouldBeApplied: values.guarantor }
        ] as const) {
            if (shouldBeApplied && !draftPerson.roles?.includes(role)) {
                draftPerson.roles = [...(draftPerson.roles ?? []), role];
            } else if (!shouldBeApplied && draftPerson.roles?.includes(role)) {
                draftPerson.roles = draftPerson.roles!.filter(
                    (r) => r !== role
                );
            }
        }

        const serialisedAddress: TSerialisedAddress = {
            ...(draftPerson.residential_addresses?.[0] ?? {}),
            uid: values.address.uid ? values.address.uid : uuidv4()
        };

        if (values.address.house_number)
            serialisedAddress.house_number = values.address.house_number;
        if (values.address.house_name)
            serialisedAddress.house_name = values.address.house_name;
        if (values.address.street_line_1)
            serialisedAddress.street_line_1 = values.address.street_line_1;
        if (values.address.street_line_2)
            serialisedAddress.street_line_2 = values.address.street_line_2;
        if (values.address.town) serialisedAddress.town = values.address.town;
        if (values.address.postcode)
            serialisedAddress.postcode = values.address.postcode;
        if (values.address.equifax_token)
            serialisedAddress.equifax_token = values.address.equifax_token;

        draftPerson.residential_addresses = [serialisedAddress];
    });
};
